<template>
  <div class="motor-page motor-page--customer">
    <header class="motor-header">
      <div class="motor-header-left">
        <button type="button" class="back-btn" @click="goBack">
          <v-icon size="18" color="#1E4276">mdi-arrow-left</v-icon>
        </button>
        <div>
          <p class="breadcrumb">{{ motorConfig.labels.applyBreadcrumb }}</p>
          <h1 class="title">{{ motorConfig.labels.applyTitle }}</h1>
        </div>
      </div>
      <button type="button" class="btn-new-sale" @click="showNewSaleConfirm = true">
        <v-icon x-small color="#1E4276" class="tw-mr-1">mdi-refresh</v-icon>
        Start over
      </button>
    </header>

    <referral-agent-banner />

    <v-dialog v-model="showNewSaleConfirm" max-width="380" overlay-opacity="0.5">
      <div class="new-sale-dialog">
        <div class="nsd-header">
          <div class="nsd-icon">
            <v-icon size="24" color="white">mdi-alert-circle-outline</v-icon>
          </div>
          <div>
            <p class="nsd-title">Start over?</p>
            <p class="nsd-sub">{{ motorConfig.labels.newSaleSub }}</p>
          </div>
        </div>
        <div class="nsd-body">
          <p class="nsd-desc">Unsaved details in this session will be removed. Existing saved quotes remain in the system.</p>
        </div>
        <div class="nsd-footer">
          <button type="button" class="nsd-btn-cancel" @click="showNewSaleConfirm = false">Keep Working</button>
          <button type="button" class="nsd-btn-confirm" @click="startNewSale">
            <v-icon x-small color="white" class="tw-mr-1">mdi-refresh</v-icon>
            Yes, Start Over
          </button>
        </div>
      </div>
    </v-dialog>

    <div v-if="loading" class="loading-wrap">
      <v-progress-circular indeterminate color="#00AEEF" size="48" />
    </div>

    <template v-else>
      <div class="step-progress">
        <div
          v-for="(label, i) in motorConfig.saleSteps"
          :key="label"
          class="step-item"
          :class="{ active: activeStep === i, done: activeStep > i }"
        >
          <span class="step-dot">{{ activeStep > i ? '✓' : i + 1 }}</span>
          <span class="step-label">{{ label }}</span>
        </div>
      </div>

      <div class="quote-layout" :class="{ 'quote-layout--checkout': activeStep === 3 }">
        <div class="quote-form-column">
          <!-- Step 0: Details -->
          <div v-show="activeStep === 0" class="section-card">
            <div class="section-header">
              <span class="section-number">1</span>
              <h3>Your details</h3>
            </div>
            <p class="step-hint">We confirm your identity from your Pacis account and national registry records.</p>

            <div class="motor-form-fields">
              <div class="form-row form-row--single">
                <div class="input-group">
                  <label class="input-label">National ID <span class="req">*</span></label>
                  <div class="input-with-btn">
                    <v-text-field
                      v-model="form.idNumber"
                      placeholder="e.g. 12345678"
                      type="number"
                      outlined
                      dense
                      hide-details="auto"
                      class="custom-input"
                      maxlength="9"
                      :disabled="fetchingIdentity || identityFetched"
                      @keyup.enter="verifyIdentity"
                      @blur="form.idNumber = (form.idNumber || '').replace(/\D/g, '').slice(0, 9)"
                    />
                    <button
                      type="button"
                      class="verify-btn"
                      :disabled="fetchingIdentity || !form.idNumber || identityFetched"
                      @click="verifyIdentity"
                    >
                      <v-progress-circular v-if="fetchingIdentity" indeterminate size="14" width="2" color="white" />
                      <template v-else><v-icon small color="white">mdi-magnify</v-icon> Verify</template>
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="nameMismatch" class="identity-banner identity-banner--error">
                <v-icon size="18" color="#DC2626" class="tw-mr-2">mdi-alert-circle-outline</v-icon>
                <div>
                  The name on your Pacis account does not match IPRS records for this ID. Check your ID or contact support.
                </div>
              </div>

              <div v-if="iprsUnavailable && identityFetched && !nameMismatch" class="identity-banner identity-banner--warn">
                <v-icon size="18" color="#D97706" class="tw-mr-2">mdi-database-alert-outline</v-icon>
                <div>
                  Identity registry is temporarily unavailable. Enter your details manually and upload your National ID.
                </div>
              </div>

              <div v-if="idVerified" class="verified-details-grid">
                <div class="verified-detail-row">
                  <span>Full name</span>
                  <span>{{ form.fullName || '—' }}</span>
                </div>
                <div v-if="form.gender" class="verified-detail-row">
                  <span>Gender</span>
                  <span>{{ form.gender === 'M' || form.gender === 'MALE' ? 'Male' : 'Female' }}</span>
                </div>
                <div class="verified-detail-row">
                  <span>ID number</span>
                  <span>{{ form.idNumber }}</span>
                </div>
              </div>

              <div v-if="iprsUnavailable && identityFetched && !nameMismatch" class="form-row form-row--single tw-mt-3">
                <div class="input-group">
                  <label class="input-label">Full name <span class="req">*</span></label>
                  <v-text-field
                    v-model="form.fullName"
                    placeholder="As on ID"
                    outlined
                    dense
                    hide-details="auto"
                    class="custom-input"
                  />
                </div>
              </div>

              <div v-if="identityFetched && !nameMismatch && (iprsUnavailable || !kraVerified)" class="form-row form-row--single tw-mt-3">
                <div class="input-group">
                  <label class="input-label">KRA PIN <span class="req">*</span></label>
                  <v-text-field
                    v-model="form.kraPinNumber"
                    placeholder="e.g. A012345678Z"
                    outlined
                    dense
                    hide-details="auto"
                    maxlength="11"
                    class="custom-input tw-uppercase"
                    :readonly="kraVerified"
                    :append-icon="kraVerified ? 'mdi-lock-outline' : undefined"
                    @blur="!kraVerified && (form.kraPinNumber = sanitizeKraPin(form.kraPinNumber))"
                  />
                  <p v-if="!kraVerified" class="field-hint-warn tw-mt-1">
                    Tax registration could not be verified automatically. Enter your KRA PIN manually.
                  </p>
                  <p v-if="form.kraPinNumber && !validateKraPin(form.kraPinNumber)" class="field-hint-warn tw-mt-1">
                    Enter a valid KRA PIN (e.g. A012345678Z)
                  </p>
                </div>
              </div>

              <motor-document-upload-panel
                v-if="identityFetched && !nameMismatch && requiresIdentityDocs"
                :uploads="uploads"
                :show-logbook="false"
                :requires-id="requiresIdUpload"
                :requires-kra="requiresKraUpload"
                hint="Upload proof documents where registry verification was unavailable."
                class="tw-mt-4"
                @file-selected="onUploadSelected"
                @remove="clearUpload"
              />

              <v-form v-if="identityFetched && !nameMismatch" ref="detailsForm" v-model="detailsValid" class="tw-mt-4">
                <p class="subsection-title">Contact details</p>
                <div class="form-row">
                  <div class="input-group">
                    <label class="input-label">Mobile <span class="req">*</span></label>
                    <v-text-field v-model="form.mobileNumber" placeholder="07XXXXXXXX" outlined dense class="custom-input" :rules="[required('Mobile'), kenyaMobileFormat()]" />
                  </div>
                  <div class="input-group">
                    <label class="input-label">Email <span class="req">*</span></label>
                    <v-text-field v-model="form.email" type="email" outlined dense hide-details="auto" class="custom-input" :rules="[required('Email'), emailFormat()]" />
                  </div>
                </div>
              </v-form>
            </div>

            <div class="actions-row">
              <button type="button" class="btn-primary" :disabled="!canSaveDetails || saving" @click="saveDetails">
                {{ saving ? 'Saving…' : 'Next' }}
                <v-icon small color="white" class="tw-ml-1">mdi-arrow-right</v-icon>
              </button>
            </div>
          </div>

          <!-- Step 1: Vehicle -->
          <div v-show="activeStep === 1" class="section-card">
            <div class="section-header">
              <span class="section-number">2</span>
              <h3>Vehicle information</h3>
            </div>
            <p class="step-hint">Enter your cover start date and registration number, then verify against NTSA and DMVIC.</p>

            <div class="verify-info-strip">
              <v-icon small color="#0284c7">mdi-shield-check-outline</v-icon>
              <div>
                This checks <strong>NTSA</strong> records and runs <strong>DMVIC</strong> double-insurance screening for your chosen cover start date.
              </div>
            </div>

            <div class="motor-form-fields">
              <div class="form-row form-row--single">
                <motor-cover-start-field v-model="coverStartDate" />
              </div>
              <div class="form-row form-row--single">
                <div class="input-group">
                  <label class="input-label">Registration no. <span class="req">*</span></label>
                  <div class="input-with-btn">
                    <v-text-field
                      v-model="vehicle.registrationNumber"
                      placeholder="e.g. KCD 123A"
                      outlined
                      dense
                      hide-details="auto"
                      class="custom-input"
                      maxlength="10"
                      :disabled="fetchingVehicle"
                      @keyup.enter="verifyVehicle"
                      @blur="vehicle.registrationNumber = sanitizeReg(vehicle.registrationNumber)"
                    />
                    <button type="button" class="verify-btn" :disabled="fetchingVehicle || !vehicle.registrationNumber || !coverStartDate" @click="verifyVehicle">
                      <v-progress-circular v-if="fetchingVehicle" indeterminate size="14" width="2" color="white" />
                      <template v-else><v-icon small color="white">mdi-magnify</v-icon> Verify</template>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="fetchingVehicle" class="verify-progress-strip">
              <v-progress-circular indeterminate size="12" width="2" color="#00AEEF" class="tw-mr-1" />
              <span>{{ fetchingNtsa ? 'Fetching NTSA vehicle data…' : 'Checking insurance status…' }}</span>
            </div>

            <div v-if="vehicleVerified && verifyError === 'double_insurance'" class="double-insure-block">
              <p class="di-title">Active insurance detected</p>
              <p class="di-subtitle">
                The selected cover start overlaps with existing cover.
                <span v-if="doubleInsuranceDetails?.coverEndDate"> Existing policy expires {{ doubleInsuranceDetails.coverEndDate }}.</span>
                <span v-if="doubleInsuranceEarliestStart"> Choose a start date on or after {{ formatDisplayDate(doubleInsuranceEarliestStart) }}.</span>
              </p>
            </div>

            <div v-if="vehicleVerified && verifyError !== 'double_insurance'">
              <div v-if="ntsaFound && !mismatchDetected" class="ntsa-success-banner">
                <v-icon size="18" color="#059669" class="tw-mr-2">mdi-check-decagram</v-icon>
                <div>
                  <p class="nsb-title">NTSA record found</p>
                  <p class="nsb-sub">Vehicle confirmed and insurance check passed.</p>
                </div>
              </div>
              <div v-else-if="ntsaUnavailable && !mismatchDetected" class="ntsa-manual-banner">
                <v-icon size="18" color="#D97706" class="tw-mr-2">mdi-cloud-off-outline</v-icon>
                <div>
                  <p class="nsb-title">NTSA search unavailable</p>
                  <p class="nsb-sub">{{ ntsaStatusMessage || 'The NTSA service did not respond. Enter vehicle details manually below to continue.' }}</p>
                </div>
              </div>
              <div v-else-if="!mismatchDetected" class="ntsa-manual-banner">
                <v-icon size="18" color="#D97706" class="tw-mr-2">mdi-car-search-outline</v-icon>
                <div>
                  <p class="nsb-title">NTSA record not found</p>
                  <p class="nsb-sub">Insurance check passed. Enter vehicle details manually below.</p>
                </div>
              </div>
              <div v-if="mismatchDetected" class="alert-box alert-warning">
                <v-icon size="18" color="#F59E0B" class="tw-mr-2">mdi-alert-circle-outline</v-icon>
                <div>
                  <p class="nsb-title">Vehicle mismatch</p>
                  <p class="nsb-sub">
                    Registration <strong>{{ vehicle.registrationNumber }}</strong> does not match
                    {{ quoteVehicleLine }}. Go back and start a new quote with the correct vehicle.
                  </p>
                </div>
              </div>
            </div>

            <v-form v-if="showVehicleDetailFields" ref="vehicleForm" v-model="vehicleValid">
              <div v-if="ntsaFound" class="verified-grid">
                <div class="verified-field"><span>Chassis</span><span>{{ vehicle.chassisNumber || '—' }}</span></div>
                <div class="verified-field"><span>Engine</span><span>{{ vehicle.engineNumber || '—' }}</span></div>
                <div class="verified-field"><span>Engine CC</span><span>{{ vehicle.cc || '—' }}</span></div>
                <div class="verified-field"><span>Colour</span><span>{{ vehicle.color || '—' }}</span></div>
                <div class="verified-field"><span>Seats</span><span>{{ vehicle.seatingCapacity || '—' }}</span></div>
              </div>
              <div class="motor-form-fields">
                <template v-if="!ntsaFound">
                  <div class="form-row">
                    <div class="input-group">
                      <label class="input-label">Engine no. <span class="req">*</span></label>
                      <v-text-field
                        v-model="vehicle.engineNumber"
                        placeholder="e.g. 2TR1234567"
                        outlined
                        dense
                        hide-details="auto"
                        class="custom-input"
                        maxlength="17"
                        :rules="engineNoRules"
                        @blur="vehicle.engineNumber = sanitizeEngine(vehicle.engineNumber)"
                      />
                    </div>
                    <div class="input-group">
                      <label class="input-label">Chassis no. <span class="req">*</span></label>
                      <v-text-field
                        v-model="vehicle.chassisNumber"
                        placeholder="e.g. JTEBU5JR0E5123456"
                        outlined
                        dense
                        hide-details="auto"
                        class="custom-input"
                        maxlength="20"
                        :rules="chassisNoRules"
                        @blur="vehicle.chassisNumber = sanitizeChassis(vehicle.chassisNumber)"
                      />
                    </div>
                  </div>
                  <div class="form-row">
                    <div class="input-group">
                      <label class="input-label">Engine CC <span class="req">*</span></label>
                      <v-text-field
                        v-model="vehicle.cc"
                        placeholder="e.g. 1800"
                        type="number"
                        outlined
                        dense
                        hide-details="auto"
                        class="custom-input"
                        maxlength="4"
                        :rules="engineCcRules"
                        @blur="vehicle.cc = clampInt(vehicle.cc, 50, 9999)"
                      />
                    </div>
                    <div class="input-group">
                      <label class="input-label">Colour <span class="req">*</span></label>
                      <v-text-field
                        v-model="vehicle.color"
                        placeholder="e.g. Silver"
                        outlined
                        dense
                        hide-details="auto"
                        class="custom-input"
                        maxlength="20"
                        :rules="colorRules"
                        @blur="vehicle.color = sanitizeAlpha(vehicle.color, 20)"
                      />
                    </div>
                  </div>
                  <div class="form-row form-row--single">
                    <div class="input-group">
                      <label class="input-label">Seating capacity <span class="req">*</span></label>
                      <v-text-field
                        v-model="vehicle.seatingCapacity"
                        placeholder="e.g. 5"
                        type="number"
                        outlined
                        dense
                        hide-details="auto"
                        class="custom-input"
                        maxlength="3"
                        :rules="seatingRules"
                        @blur="vehicle.seatingCapacity = clampInt(vehicle.seatingCapacity, 1, 100)"
                      />
                    </div>
                  </div>
                </template>
                <motor-logbook-fields
                  :log-book-number.sync="vehicle.logBookNumber"
                  :transferred.sync="logbookTransferred"
                  class="tw-mt-2"
                />
                <motor-document-upload-panel
                  :uploads="uploads"
                  :requires-id="false"
                  :requires-kra="false"
                  hint="Upload a clear copy of your vehicle logbook."
                  class="tw-mt-4"
                  @file-selected="onUploadSelected"
                  @remove="clearUpload"
                />
                <motor-valuer-picker
                  v-if="isComprehensive"
                  class="tw-mt-5"
                  :cover-start-date="coverStartDate"
                  :location-filter.sync="valuerLocationFilter"
                  :search-query.sync="valuerSearchQuery"
                  :selected-name="selectedValuer"
                  :valuation-date.sync="valuationDate"
                  @select="onValuerSelect"
                />
              </div>
              <div class="actions-row">
                <button type="button" class="btn-outline" @click="activeStep = 0">Back</button>
                <button type="button" class="btn-primary" :disabled="!canSaveVehicle" @click="saveVehicle">
                  {{ saving ? 'Saving…' : 'Next' }}
                </button>
              </div>
            </v-form>
            <div v-else-if="!fetchingVehicle" class="actions-row">
              <button type="button" class="btn-outline" @click="activeStep = 0">Back</button>
            </div>
          </div>

          <!-- Step 2: Branch -->
          <div v-show="activeStep === 2" class="section-card">
            <div class="section-header">
              <span class="section-number">3</span>
              <h3>Service branch</h3>
            </div>
            <p class="step-hint">Choose your preferred Pacis branch. You will receive a digital certificate by email.</p>
            <div
              v-if="branches.length"
              class="branch-grid"
              role="radiogroup"
              aria-label="Service branch"
            >
              <button
                v-for="b in branches"
                :key="b.code"
                type="button"
                role="radio"
                :aria-checked="String(branchCode) === String(b.code)"
                :class="['branch-card', String(branchCode) === String(b.code) ? 'branch-card--selected' : '']"
                @click="selectBranch(b.code)"
              >
                <span class="branch-card-indicator" aria-hidden="true">
                  <span class="branch-card-dot" />
                </span>
                <span class="branch-card-body">
                  <span class="branch-card-name">{{ formatBranchName(b) }}</span>
                  <span v-if="formatBranchAddress(b)" class="branch-card-addr">{{ formatBranchAddress(b) }}</span>
                  <span v-if="b.tel" class="branch-card-meta">{{ b.tel }}</span>
                  <span v-if="b.openingTime || b.closingTime" class="branch-card-meta">
                    Open {{ b.openingTime || '—' }} · Close {{ b.closingTime || '—' }}
                  </span>
                </span>
              </button>
            </div>
            <v-progress-circular v-else indeterminate color="#00AEEF" class="branch-loading" />
            <div class="actions-row">
              <button type="button" class="btn-outline" @click="activeStep = 1">Back</button>
              <button type="button" class="btn-primary" :disabled="!branchCode || saving" @click="saveBranchAndCreatePolicy">
                {{ saving ? 'Preparing payment…' : 'Continue to payment' }}
              </button>
            </div>
          </div>

          <!-- Step 3: Payment -->
          <div v-show="activeStep === 3" class="section-card section-card--payment">
            <div class="section-header">
              <span class="section-number">4</span>
              <h3>Payment</h3>
            </div>

            <div class="pay-summary-card tw-mb-5">
              <div class="pay-summary-head">
                <div class="pay-summary-head-icon">
                  <v-icon x-small color="white">mdi-car-outline</v-icon>
                </div>
                <span>Your cover</span>
              </div>
              <div class="pay-summary-section">
                <div class="pay-sum-row">
                  <span class="pay-sum-label">Cover</span>
                  <span class="pay-sum-value">{{ coverLabel }}</span>
                </div>
                <div class="pay-sum-row">
                  <span class="pay-sum-label">Vehicle</span>
                  <span class="pay-sum-value">{{ vehicleSummary }}</span>
                </div>
                <div v-if="effectiveQuoteId" class="pay-sum-row">
                  <span class="pay-sum-label">Quote ref.</span>
                  <span class="pay-sum-value">{{ effectiveQuoteId }}</span>
                </div>
              </div>
              <div class="pay-premium-footer">
                <div class="pay-prem-row">
                  <span>Annual Premium</span>
                  <span class="tw-font-bold tw-text-[#1E4276]">{{ formattedPremium }}</span>
                </div>
                <div class="pay-prem-total">
                  <span>Amount Due Now</span>
                  <span class="pay-prem-total-val">{{ formattedAmountDueNow }}</span>
                </div>
              </div>
            </div>

            <!-- PAYMENT PLAN (Comprehensive only) -->
            <div v-if="isComprehensive" class="tw-mb-6">
              <p class="section-sub-label">Select Payment Plan</p>
              <div class="plan-grid-v2">
                <label
                  v-for="plan in paymentPlans"
                  :key="plan.value"
                  :class="[
                    'plan-card-v2',
                    paymentPlan === plan.value ? 'plan-v2-active' : '',
                    plan.disabled ? 'plan-v2-disabled' : '',
                  ]"
                >
                  <input
                    v-model="paymentPlan"
                    type="radio"
                    :value="plan.value"
                    class="tw-hidden"
                    :disabled="plan.disabled"
                  >
                  <div
                    class="plan-v2-icon-wrap"
                    :class="paymentPlan === plan.value ? 'plan-v2-icon-active' : ''"
                  >
                    <v-icon
                      size="20"
                      :color="paymentPlan === plan.value ? '#00AEEF' : '#94a3b8'"
                    >
                      {{ plan.icon }}
                    </v-icon>
                  </div>
                  <div class="plan-v2-body">
                    <p class="plan-v2-name">{{ plan.label }}</p>
                    <p class="plan-v2-amount">{{ plan.amountLine }}</p>
                    <div
                      v-if="plan.value === 'installments' && paymentPlan === 'installments'"
                      class="installment-breakdown tw-mt-2"
                    >
                      <div class="inst-chip inst-chip-now">
                        <span class="inst-chip-pct">40%</span>
                        <span class="inst-chip-label">Now</span>
                        <span class="inst-chip-amount">{{ fmtCurrency(installmentFirst) }}</span>
                      </div>
                      <div class="inst-chip-connector">→</div>
                      <div class="inst-chip inst-chip-later">
                        <span class="inst-chip-pct">30%</span>
                        <span class="inst-chip-label">Month 2</span>
                        <span class="inst-chip-amount">{{ fmtCurrency(installmentLater) }}</span>
                      </div>
                      <div class="inst-chip-connector">→</div>
                      <div class="inst-chip inst-chip-later">
                        <span class="inst-chip-pct">30%</span>
                        <span class="inst-chip-label">Month 3</span>
                        <span class="inst-chip-amount">{{ fmtCurrency(installmentLater) }}</span>
                      </div>
                    </div>
                    <p
                      v-if="plan.value === 'installments'"
                      class="tw-text-xs tw-text-[#94a3b8] tw-mt-2"
                    >
                      Balance to be cleared within 2 months.
                    </p>
                  </div>
                  <div v-if="paymentPlan === plan.value" class="plan-v2-check">
                    <v-icon x-small color="white">mdi-check</v-icon>
                  </div>
                </label>
              </div>
            </div>

            <v-checkbox v-model="declarationsAccepted" hide-details class="pay-decl-check tw-mb-4">
              <template #label>
                <span class="decl-text">I confirm the information is accurate and I accept the terms.</span>
              </template>
            </v-checkbox>

            <p class="section-sub-label tw-mb-3">Payment method</p>
            <div class="pay-method-tabs tw-mb-4">
              <button
                type="button"
                :class="['pay-method-tab', activePayTab === 'mpesa' ? 'pay-tab-active' : '']"
                @click="activePayTab = 'mpesa'"
              >
                <img src="/mpesa.jpeg" alt="M-Pesa" class="pay-tab-mpesa-logo" />
                <span>M-Pesa STK</span>
              </button>
              <button
                type="button"
                :class="['pay-method-tab', activePayTab === 'paybill' ? 'pay-tab-active' : '']"
                @click="activePayTab = 'paybill'"
              >
                <img src="/mpesa.jpeg" alt="M-Pesa" class="pay-tab-mpesa-logo" />
                <span>Paybill</span>
              </button>
            </div>

            <div v-if="activePayTab === 'mpesa'" class="pay-panel">
              <div class="mpesa-panel-card tw-mb-4">
                <div class="mpesa-panel-header">
                  <img src="/mpesa.jpeg" alt="M-Pesa" class="mpesa-panel-logo" />
                  <div>
                    <p class="mpesa-panel-title">Lipa Na M-Pesa</p>
                    <p class="mpesa-panel-sub">STK Push — you will receive a prompt on your phone</p>
                  </div>
                </div>
                <label class="input-label tw-mt-4">M-Pesa phone number <span class="req">*</span></label>
                <v-text-field
                  v-model="mpesaPhone"
                  placeholder="e.g. 0712345678"
                  type="tel"
                  outlined
                  dense
                  hide-details="auto"
                  maxlength="10"
                  class="custom-input"
                  :disabled="paying"
                />
                <p class="pay-ref-chip tw-mt-3">
                  <span class="pay-ref-label">Account ref.</span>
                  <span class="pay-ref-code">{{ paymentReference }}</span>
                </p>
              </div>
              <button
                type="button"
                class="btn-pay tw-w-full"
                :disabled="paying || !canPay"
                @click="payStk"
              >
                <v-progress-circular
                  v-if="paying"
                  indeterminate
                  size="16"
                  width="2"
                  color="white"
                  class="tw-mr-2"
                />
                <template v-else>
                  <v-icon small color="white" class="tw-mr-2">mdi-cellphone-wireless</v-icon>
                </template>
                {{ paying ? 'Waiting for M-Pesa…' : `Send STK Push · ${formattedAmountDueNow}` }}
              </button>
              <p class="pay-stk-hint">
                <v-icon x-small color="#94a3b8" class="pay-stk-hint-icon">mdi-information-outline</v-icon>
                <span class="pay-stk-hint-text">
                  A payment prompt will appear on
                  <strong class="pay-stk-phone">{{ mpesaPhone || 'your phone' }}</strong>.
                  Enter your M-Pesa PIN to confirm.
                </span>
              </p>
            </div>

            <div v-if="activePayTab === 'paybill'" class="pay-panel">
              <div v-if="paybillFallback" class="paybill-fallback-alert tw-mb-4">
                <v-icon size="16" color="#D97706" class="tw-mr-2 tw-flex-shrink-0">mdi-alert-circle-outline</v-icon>
                <span>STK Push failed or timed out. Complete payment via Paybill below.</span>
              </div>
              <div class="paybill-panel-card tw-mb-4">
                <div class="paybill-panel-header">
                  <img src="/mpesa.jpeg" alt="M-Pesa" class="mpesa-panel-logo" />
                  <div>
                    <p class="paybill-panel-title">M-Pesa Paybill</p>
                    <p class="paybill-panel-sub">Lipa Na M-Pesa → Pay Bill</p>
                  </div>
                </div>
                <div class="paybill-ussd-steps">
                  <p class="paybill-ussd-title">
                    <v-icon x-small color="#64748b" class="tw-mr-1">mdi-cellphone-text</v-icon>
                    Quick steps
                  </p>
                  <div class="paybill-ussd-list">
                    <div v-for="(step, i) in ussdSteps" :key="i" class="paybill-ussd-item">
                      <span class="paybill-ussd-num">{{ i + 1 }}</span>
                      <span v-html="step" />
                    </div>
                  </div>
                </div>
              </div>
              <button
                type="button"
                class="btn-amber btn-amber--block"
                :disabled="paying || !canPayPaybill"
                @click="verifyPaybill"
              >
                <v-progress-circular
                  v-if="paying"
                  indeterminate
                  size="16"
                  width="2"
                  color="#1E4276"
                  class="tw-mr-2"
                />
                <template v-else>
                  <v-icon small color="#1E4276" class="tw-mr-2">mdi-check-circle-outline</v-icon>
                </template>
                {{ paying ? 'Validating payment…' : 'Validate payment' }}
              </button>
            </div>

            <div class="actions-row tw-mt-4">
              <button type="button" class="btn-outline" @click="activeStep = 2">Back</button>
            </div>
          </div>
        </div>

        <aside v-if="activeStep < 3" class="quote-sidebar">
          <motor-quote-sidebar
            :cover-label="coverLabel"
            :vehicle-line="vehicleSummary"
            :premium="totalPremium"
            :rate-applied="activeRisk?.rateApplied"
            :covertype-id="activeRisk?.covertypeId"
            :quote-id="effectiveQuoteId"
            :quote="exportQuote"
            channel="customer"
          />
        </aside>
      </div>
    </template>
  </div>
</template>

<script>
import MotorQuoteSidebar from '@/components/motor/MotorQuoteSidebar';
import MotorCoverStartField from '@/components/motor/MotorCoverStartField';
import MotorLogbookFields from '@/components/motor/MotorLogbookFields';
import MotorDocumentUploadPanel from '@/components/motor/MotorDocumentUploadPanel';
import MotorValuerPicker from '@/components/motor/MotorValuerPicker';
import ReferralAgentBanner from '@/components/shared/ReferralAgentBanner';
import { MOTOR_COVER_OPTIONS, MOTOR_PROD_ID, MOTOR_COVER_COMPREHENSIVE } from '@/constants/motorPrivate';
import {
  getMotorQuote,
  updateMotorQuote,
  initMotorQuotationPolicy,
  listMotorBranches,
  requestMotorStkPush,
  pollMotorPaymentStatus,
} from '@/services/customerMotorApi';
import { referralAgencyNumericId } from '@/services/referralApi';
import motorPaybillApi from '@/services/motorPaybillApi';
import instalmentAmount from '@/utils/instalments';
import {
  saveMotorPendingQuote,
  saveMotorSaleProgress,
  loadMotorSaleProgress,
  loadMotorPendingQuote,
  clearMotorPendingQuote,
} from '@/utils/saleCache';
import { getMotorChannelConfig, getCognitoIdentityProfile } from '@/utils/motorChannel';
import { buildMotorQuoteExportPayload } from '@/utils/motorQuoteDocument';
import { formatDate } from '@/utils/time';
import {
  sanitizeReg, sanitizeChassis, sanitizeEngine, sanitizeLogbook, sanitizeKraPin, sanitizeAlpha,
  clampInt, validateKraPin,
} from '@/utils/sanitize';
import {
  logbookFieldsValid,
  manualVehicleFieldsValid,
  motorChassisNoRules,
  motorColorRules,
  motorEngineCcRules,
  motorEngineNoRules,
  motorSeatingRules,
} from '@/utils/motorVehicleValidation';
import {
  clampCoverStartDate, minCoverStartDate,
} from '@/utils/motorCoverDates';
import { verifyMotorIdentity } from '@/utils/motorIdentityVerify';
import { validateNationalId } from '@/utils/paValidation';
import {
  applyBenefitSyncToQuote,
  fetchMotorChargeableBenefits,
  restoreBenefitSelectionFromRisk,
  syncMotorQuoteBenefits,
} from '@/utils/motorBenefits';
import {
  createMotorUploadState, uploadMotorDocumentByKey,
} from '@/utils/motorDocumentUpload';
import {
  runDoubleInsuranceCheck,
  fetchNtsaVehicle,
  mapNtsaToVehicle,
  detectQuoteMakeMismatch,
  NTSA_OUTCOME,
} from '@/utils/motorVehicleVerify';
import validations from '@/utils/validations';
import { mapGetters } from 'vuex';
import axios from 'axios';

const POLICY_PREPARE_ERROR = 'Could not prepare policy for payment';

export default {
  name: 'MotorApplyPage',
  components: {
    MotorQuoteSidebar,
    MotorCoverStartField,
    MotorLogbookFields,
    MotorDocumentUploadPanel,
    MotorValuerPicker,
    ReferralAgentBanner,
  },
  data() {
    return {
      motorConfig: getMotorChannelConfig(),
      ...validations,
      loading: true,
      saving: false,
      paying: false,
      activeStep: 0,
      showNewSaleConfirm: false,
      quote: {},
      activeRisk: null,
      riskId: null,
      branches: [],
      branchCode: null,
      policyCreated: false,
      detailsValid: false,
      vehicleValid: false,
      declarationsAccepted: false,
      activePayTab: 'mpesa',
      paybillFallback: false,
      paymentPlan: 'full',
      mpesaPhone: '',
      pollTimer: null,
      fetchingVehicle: false,
      fetchingNtsa: false,
      vehicleVerified: false,
      ntsaFound: false,
      ntsaUnavailable: false,
      ntsaStatusMessage: '',
      verifyError: null,
      doubleInsuranceDetails: null,
      mismatchDetected: false,
      coverStartDate: minCoverStartDate(),
      logbookTransferred: 'yes',
      valuerLocationFilter: '',
      valuerSearchQuery: '',
      selectedValuer: '',
      selectedValuerData: null,
      valuationDate: '',
      fetchingIdentity: false,
      identityFetched: false,
      idVerified: false,
      kraVerified: false,
      iprsUnavailable: false,
      nameMismatch: false,
      doubleInsuranceEarliestStart: null,
      uploads: createMotorUploadState(),
      cognitoFirstName: '',
      form: {
        fullName: '',
        mobileNumber: '',
        email: '',
        idNumber: '',
        kraPinNumber: '',
        gender: '',
      },
      vehicle: {
        registrationNumber: '',
        engineNumber: '',
        chassisNumber: '',
        cc: '',
        color: '',
        seatingCapacity: '5',
        logBookNumber: '',
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    ...mapGetters('referral', ['referralAgent']),
    referralAgentId() {
      return referralAgencyNumericId(this.referralAgent);
    },
    effectiveQuoteId() {
      return this.$route.query.quoteId || this.quote.quoteId;
    },
    coverLabel() {
      const id = Number(this.activeRisk?.covertypeId);
      return MOTOR_COVER_OPTIONS.find((o) => o.covertypeId === id)?.label || 'Motor';
    },
    isComprehensive() {
      return Number(this.activeRisk?.covertypeId) === MOTOR_COVER_COMPREHENSIVE;
    },
    requiresIdUpload() {
      return this.iprsUnavailable || !this.idVerified;
    },
    requiresKraUpload() {
      return !this.kraVerified;
    },
    requiresIdentityDocs() {
      return this.requiresIdUpload || this.requiresKraUpload;
    },
    canSaveDetails() {
      if (!this.identityFetched || this.nameMismatch || this.fetchingIdentity) return false;
      if (this.iprsUnavailable && !(this.form.fullName || '').trim()) return false;
      if (!this.idVerified && !this.iprsUnavailable) return false;
      if (!this.form.kraPinNumber || !validateKraPin(this.form.kraPinNumber)) return false;
      if (this.requiresIdUpload && !this.uploads.nationalId.done) return false;
      if (this.requiresKraUpload && !this.uploads.kra.done) return false;
      return this.detailsValid;
    },
    vehicleSummary() {
      const c = this.activeRisk?.car || {};
      return [c.make, c.model, c.registrationNumber].filter(Boolean).join(' · ') || '—';
    },
    totalPremium() {
      return this.activeRisk?.riskPremDto?.totalPremium || 0;
    },
    formattedPremium() {
      return this.fmtCurrency(this.totalPremium);
    },
    amountDueNow() {
      if (!this.isComprehensive || this.paymentPlan === 'full') return this.totalPremium;
      if (this.paymentPlan === 'installments') return this.installmentFirst;
      if (this.paymentPlan === 'ipf') return this.totalPremium * 0.2;
      return this.totalPremium;
    },
    formattedAmountDueNow() {
      return this.fmtCurrency(this.amountDueNow);
    },
    installmentFirst() {
      return instalmentAmount(this.totalPremium, 40);
    },
    installmentLater() {
      return instalmentAmount(this.totalPremium, 30);
    },
    paymentPlans() {
      const p40 = this.installmentFirst;
      const p30 = this.installmentLater;
      const plans = [
        {
          value: 'full',
          icon: 'mdi-shield-check',
          label: 'Full Payment',
          amountLine: `Pay ${this.fmtCurrency(this.totalPremium)} now — best value`,
          disabled: false,
        },
        {
          value: 'installments',
          icon: 'mdi-calendar-month',
          label: 'Installments  (40 : 30 : 30)',
          amountLine: `Pay ${this.fmtCurrency(p40)} now, then ${this.fmtCurrency(p30)} × 2`,
          disabled: false,
        },
        {
          value: 'ipf',
          icon: 'mdi-clock-time-four-outline',
          label: 'IPF (Coming Soon)',
          amountLine: 'External financing — available soon',
          disabled: true,
        },
      ];
      if (!this.isComprehensive) {
        return plans.filter((p) => p.value === 'full');
      }
      return plans;
    },
    paymentReference() {
      return (this.vehicle.registrationNumber || '').replace(/\s+/g, '').toUpperCase() || String(this.effectiveQuoteId || '');
    },
    canPay() {
      return this.declarationsAccepted && this.policyCreated && this.mpesaPhone && this.paymentReference;
    },
    canPayPaybill() {
      return this.declarationsAccepted && this.policyCreated && this.paymentReference;
    },
    quoteVehicleLine() {
      const c = this.activeRisk?.car || {};
      return [c.make, c.model].filter(Boolean).join(' ') || 'your quoted vehicle';
    },
    showVehicleDetailFields() {
      return this.vehicleVerified
        && this.verifyError !== 'double_insurance'
        && !this.mismatchDetected;
    },
    canSaveVehicle() {
      if (!this.showVehicleDetailFields || this.saving) return false;
      if (!this.coverStartDate || !this.logbookTransferred) return false;
      if (!logbookFieldsValid(this.vehicle.logBookNumber, this.logbookTransferred)) return false;
      if (!this.uploads.logbook.done) return false;
      if (this.isComprehensive && (!this.selectedValuer || !this.valuationDate)) return false;
      if (this.ntsaFound) {
        return !!(
          this.vehicle.engineNumber
          && this.vehicle.chassisNumber
          && this.vehicle.cc
          && this.vehicle.color
          && this.vehicle.seatingCapacity
        );
      }
      return manualVehicleFieldsValid(this.vehicle) && this.vehicleValid;
    },
    exportQuote() {
      if (!this.effectiveQuoteId || !this.quote) return {};
      const profile = getCognitoIdentityProfile(this.authenticatedUser);
      return buildMotorQuoteExportPayload({
        quote: this.quote,
        activeRisk: this.activeRisk,
        coverLabel: this.coverLabel,
        vehicleLine: this.vehicleSummary,
        premium: this.totalPremium,
        rateApplied: this.activeRisk?.rateApplied,
        clientName: this.form.fullName || profile?.fullName || '',
        clientEmail: this.form.email || profile?.email || '',
      });
    },
    engineNoRules() {
      return motorEngineNoRules;
    },
    chassisNoRules() {
      return motorChassisNoRules;
    },
    engineCcRules() {
      return motorEngineCcRules;
    },
    colorRules() {
      return motorColorRules;
    },
    seatingRules() {
      return motorSeatingRules;
    },
    ussdSteps() {
      return [
        'Go to <strong>M-Pesa</strong> — open the <strong>M-Pesa App / SIM Toolkit</strong>, or dial <strong>*334#</strong>',
        'Select <strong>Lipa Na M-Pesa</strong> → <strong>Pay Bill</strong>',
        'Enter Business No. <strong>504700</strong>',
        `Enter Account No. <strong>${this.paymentReference || '—'}</strong>`,
        `Enter amount <strong>${this.formattedAmountDueNow}</strong>`,
        'Enter your <strong>M-Pesa PIN</strong> and confirm',
        'You will receive an <strong>M-Pesa confirmation SMS</strong> — then tap <strong>Validate payment</strong>',
      ];
    },
  },
  beforeDestroy() {
    if (this.pollTimer) clearTimeout(this.pollTimer);
  },
  async mounted() {
    await this.$store.dispatch('referral/ensureReferralValidated', this.$route.query);
    const { quoteId } = this.$route.query;
    const riskId = parseInt(this.$route.query.riskId, 10);
    if (!quoteId || !riskId) {
      this.$router.replace({ name: this.motorConfig.routes.start });
      return;
    }
    this.riskId = riskId;
    const res = await getMotorQuote(quoteId);
    if (!res.success) {
      this.$toast.error(res.message || 'Could not load quote');
      this.$router.replace({ name: this.motorConfig.routes.start });
      return;
    }
    this.quote = res.data;
    this.activeRisk = (this.quote.covertypes || []).find((c) => c.riskId === riskId)
      || this.quote.covertypes?.[0];
    if (!this.activeRisk) {
      this.$toast.error('Cover not found on quote');
      this.$router.replace({ name: this.motorConfig.routes.start });
      return;
    }
    this.mergePendingBenefits(quoteId, riskId);
    await this.resyncBenefitPremium();
    this.applyCognitoProfile();
    const email = this.authenticatedUser?.email;
    const progress = loadMotorSaleProgress(email, quoteId);
    if (progress) {
      this.form = { ...this.form, ...progress.form };
      this.vehicle = { ...this.vehicle, ...progress.vehicle };
      if (progress.branchCode) this.branchCode = progress.branchCode;
      if (progress.policyCreated) this.policyCreated = true;
      if (progress.activeStep != null) this.activeStep = progress.activeStep;
      if (progress.mpesaPhone) this.mpesaPhone = progress.mpesaPhone;
      if (progress.paymentPlan) this.paymentPlan = progress.paymentPlan;
      if (progress.coverStartDate) this.coverStartDate = progress.coverStartDate;
      if (progress.logbookTransferred) this.logbookTransferred = progress.logbookTransferred;
      if (progress.valuerLocationFilter) this.valuerLocationFilter = progress.valuerLocationFilter;
      if (progress.valuerSearchQuery) this.valuerSearchQuery = progress.valuerSearchQuery;
      if (progress.selectedValuer) this.selectedValuer = progress.selectedValuer;
      if (progress.valuationDate) this.valuationDate = progress.valuationDate;
      if (progress.idVerified) this.idVerified = progress.idVerified;
      if (progress.kraVerified) this.kraVerified = progress.kraVerified;
      if (progress.iprsUnavailable) this.iprsUnavailable = progress.iprsUnavailable;
      if (progress.identityFetched) this.identityFetched = progress.identityFetched;
    }
    if (this.quote.step === 'POLICY_CREATED') {
      this.policyCreated = true;
      this.activeStep = 3;
    }
    const branchRes = await listMotorBranches();
    if (branchRes.success) this.branches = branchRes.data;
    this.loading = false;
    this.persistProgress();
    if (this.form.idNumber && !this.identityFetched) {
      this.$nextTick(() => this.verifyIdentity());
    }
  },
  methods: {
    formatMoney(n) {
      return Number(n || 0).toLocaleString('en-KE');
    },
    fmtCurrency(n) {
      return `KES ${this.formatMoney(n)}`;
    },
    applyCognitoProfile() {
      const p = getCognitoIdentityProfile(this.authenticatedUser);
      if (!p) return;
      this.cognitoFirstName = p.firstName || '';
      if (!this.form.fullName) this.form.fullName = p.fullName;
      if (!this.form.email) this.form.email = p.email;
      if (!this.form.idNumber) this.form.idNumber = p.idNumber;
      if (!this.form.kraPinNumber) this.form.kraPinNumber = p.kraPin;
      if (!this.form.mobileNumber) this.form.mobileNumber = p.phone;
      if (!this.mpesaPhone) this.mpesaPhone = p.phone;
    },
    /** Replay selected add-ons through benefit/change so the premium includes them. */
    async resyncBenefitPremium() {
      if (!this.isComprehensive) return;
      const { addons, limits } = restoreBenefitSelectionFromRisk(this.activeRisk?.additionalBenefits);
      if (!addons.length) return;
      const benefitsRes = await fetchMotorChargeableBenefits(Number(this.activeRisk.covertypeId));
      if (!benefitsRes.success || !benefitsRes.data.length) return;
      const res = await syncMotorQuoteBenefits(this.riskId, benefitsRes.data, addons, limits);
      if (!res.success || res.data?.totalPremium == null) return;
      this.quote = applyBenefitSyncToQuote(
        this.quote, this.riskId, res.data, benefitsRes.data, addons, limits,
      );
      this.activeRisk = (this.quote.covertypes || []).find((c) => c.riskId === this.riskId)
        || this.activeRisk;
    },
    mergePendingBenefits(quoteId, riskId) {
      const email = this.authenticatedUser?.email;
      const pending = loadMotorPendingQuote(email);
      if (!pending || String(pending.quoteId) !== String(quoteId) || !pending.quote) return;
      const pendingRisk = (pending.quote.covertypes || []).find((c) => c.riskId === riskId);
      const benefits = pendingRisk?.additionalBenefits
        || this.activeRisk?.additionalBenefits;
      if (!benefits?.length) return;
      this.activeRisk = { ...this.activeRisk, additionalBenefits: benefits };
      this.quote = {
        ...this.quote,
        covertypes: (this.quote.covertypes || []).map((ct) => (
          ct.riskId === riskId ? { ...ct, additionalBenefits: benefits } : ct
        )),
      };
    },
    formatDisplayDate(iso) {
      if (!iso) return '';
      const [y, m, d] = iso.split('-');
      return `${d}/${m}/${y}`;
    },
    async verifyIdentity() {
      const idCheck = validateNationalId(this.form.idNumber);
      if (!idCheck.valid) {
        this.$toast.error(idCheck.message);
        return;
      }
      const idNumber = idCheck.normalized;
      this.form.idNumber = idNumber;
      this.fetchingIdentity = true;
      this.nameMismatch = false;
      this.identityFetched = false;
      this.idVerified = false;
      this.kraVerified = false;
      this.iprsUnavailable = false;

      try {
        const res = await verifyMotorIdentity(idNumber, this.cognitoFirstName, 'customer');
        this.identityFetched = true;
        this.iprsUnavailable = res.iprsUnavailable;
        this.nameMismatch = res.nameMismatch;
        this.idVerified = res.idVerified;
        this.kraVerified = res.kraVerified;

        if (res.nameMismatch) {
          this.$toast.error('Name on your account does not match IPRS records');
          return;
        }

        if (res.idVerified && res.iprs) {
          this.form.fullName = res.iprs.fullName || this.form.fullName;
          this.form.gender = res.iprs.gender || this.form.gender;
        }

        if (res.kraVerified && res.kraPin) {
          this.form.kraPinNumber = res.kraPin;
        } else if (this.iprsUnavailable && !this.form.kraPinNumber) {
          this.form.kraPinNumber = '';
        }

        if (this.iprsUnavailable) {
          this.$toast.info('Identity registry unavailable — enter details manually and upload your ID.');
        } else if (this.idVerified) {
          this.$toast.success('Identity verified');
        }
        this.persistProgress();
      } catch (e) {
        this.$toast.error(e?.message || 'Identity verification failed');
        this.iprsUnavailable = true;
        this.identityFetched = true;
      } finally {
        this.fetchingIdentity = false;
      }
    },
    onUploadSelected({ file, key }) {
      if (!file || !this.uploads[key]) return;
      this.uploads[key].file = file;
      this.uploads[key].done = false;
      uploadMotorDocumentByKey(this.uploads, key, this.form).catch(() => {
        this.$toast.error(`Failed to upload ${key} document`);
        this.uploads[key].done = false;
      });
    },
    clearUpload(key) {
      if (!this.uploads[key]) return;
      this.uploads[key].file = null;
      this.uploads[key].done = false;
    },
    onValuerSelect(valuer) {
      this.selectedValuer = valuer ? valuer.name : '';
      this.selectedValuerData = valuer;
      if (!valuer) this.valuationDate = '';
    },
    async uploadPendingIdentityDocs() {
      const pending = [];
      if (this.requiresIdUpload && this.uploads.nationalId.file && !this.uploads.nationalId.done) {
        pending.push(uploadMotorDocumentByKey(this.uploads, 'nationalId', this.form));
      }
      if (this.requiresKraUpload && this.uploads.kra.file && !this.uploads.kra.done) {
        pending.push(uploadMotorDocumentByKey(this.uploads, 'kra', this.form));
      }
      if (pending.length) await Promise.all(pending);
    },
    async uploadPendingLogbook() {
      if (!this.uploads.logbook.file || this.uploads.logbook.done) return;
      await uploadMotorDocumentByKey(this.uploads, 'logbook', this.form);
    },
    persistProgress() {
      const email = this.authenticatedUser?.email;
      saveMotorSaleProgress(email, this.effectiveQuoteId, {
        form: this.form,
        vehicle: this.vehicle,
        branchCode: this.branchCode,
        policyCreated: this.policyCreated,
        activeStep: this.activeStep,
        mpesaPhone: this.mpesaPhone,
        paymentPlan: this.paymentPlan,
        coverStartDate: this.coverStartDate,
        logbookTransferred: this.logbookTransferred,
        valuerLocationFilter: this.valuerLocationFilter,
        valuerSearchQuery: this.valuerSearchQuery,
        selectedValuer: this.selectedValuer,
        valuationDate: this.valuationDate,
        idVerified: this.idVerified,
        kraVerified: this.kraVerified,
        iprsUnavailable: this.iprsUnavailable,
        identityFetched: this.identityFetched,
      });
    },
    coverFromFormatted() {
      return formatDate(this.coverStartDate);
    },
    sanitizeReg,
    sanitizeChassis,
    sanitizeEngine,
    sanitizeLogbook,
    sanitizeKraPin,
    sanitizeAlpha,
    clampInt,
    validateKraPin,
    selectBranch(code) {
      this.branchCode = code;
    },
    formatBranchName(b) {
      return (b?.name || b?.shortDescription || '').trim();
    },
    formatBranchAddress(b) {
      const addr = (b?.address || '').trim();
      if (addr) return addr;
      const name = this.formatBranchName(b);
      const short = (b?.shortDescription || '').trim();
      if (short && short !== name) return short;
      return '';
    },
    async verifyVehicle() {
      const regNo = sanitizeReg(this.vehicle.registrationNumber);
      this.vehicle.registrationNumber = regNo;
      if (!regNo) {
        this.$toast.error('Enter a registration number');
        return;
      }
      this.fetchingVehicle = true;
      this.fetchingNtsa = false;
      this.vehicleVerified = false;
      this.ntsaFound = false;
      this.ntsaUnavailable = false;
      this.ntsaStatusMessage = '';
      this.verifyError = null;
      this.doubleInsuranceDetails = null;
      this.doubleInsuranceEarliestStart = null;
      this.mismatchDetected = false;

      try {
        const di = await runDoubleInsuranceCheck(axios, {
          regNo,
          chassis: this.vehicle.chassisNumber,
          coverStartIso: this.coverStartDate,
        });
        if (di.blocked) {
          this.verifyError = di.error;
          this.doubleInsuranceDetails = di.details || null;
          this.doubleInsuranceEarliestStart = di.earliestStart || null;
          this.vehicleVerified = true;
          if (di.error === 'validation_error') {
            this.$toast.error(di.message || 'Verification failed');
          } else if (di.error === 'double_insurance') {
            this.$toast.error('Cover start overlaps with existing insurance. Adjust your start date.');
          }
          return;
        }
        if (di.expired) {
          this.$toast.info('Previous insurance has expired. Proceeding with new cover.');
        }
        if (di.futureBooking) {
          this.$toast.info('Active cover detected, but your start date is after the expiry. Proceeding.');
        }

        this.fetchingNtsa = true;
        const ntsaRes = await fetchNtsaVehicle(axios, regNo);
        this.fetchingNtsa = false;

        if (ntsaRes.outcome === NTSA_OUTCOME.UNAVAILABLE) {
          this.ntsaFound = false;
          this.ntsaUnavailable = true;
          this.ntsaStatusMessage = ntsaRes.message || '';
          this.vehicleVerified = true;
          this.$toast.warning(ntsaRes.message);
          return;
        }

        if (ntsaRes.outcome === NTSA_OUTCOME.NOT_FOUND) {
          this.ntsaFound = false;
          this.ntsaUnavailable = false;
          this.vehicleVerified = true;
          return;
        }

        const mapped = mapNtsaToVehicle(ntsaRes.data);
        if (mapped) {
          this.ntsaFound = true;
          this.mismatchDetected = detectQuoteMakeMismatch(this.activeRisk?.car, mapped.make);
          if (!this.mismatchDetected) {
            this.vehicle = {
              ...this.vehicle,
              engineNumber: mapped.engineNumber || this.vehicle.engineNumber,
              chassisNumber: mapped.chassisNumber || this.vehicle.chassisNumber,
              cc: mapped.cc || this.vehicle.cc,
              color: mapped.color || this.vehicle.color,
              seatingCapacity: mapped.seatingCapacity || this.vehicle.seatingCapacity,
            };
          }
        }
        this.vehicleVerified = true;
      } catch (e) {
        this.$toast.error(e?.response?.data?.message || e?.message || 'Vehicle verification failed');
      } finally {
        this.fetchingVehicle = false;
        this.fetchingNtsa = false;
      }
    },
    normalizeGenderForApi(gender) {
      const map = {
        M: 'MALE', F: 'FEMALE', MALE: 'MALE', FEMALE: 'FEMALE',
      };
      return map[gender] || 'MALE';
    },
    normalizeMobileForApi(mobile) {
      const digits = String(mobile || '').replace(/\D/g, '');
      if (digits.startsWith('254') && digits.length >= 12) return `+${digits.slice(0, 12)}`;
      if (digits.startsWith('0') && digits.length === 10) return `+254${digits.slice(1)}`;
      if (digits.startsWith('7') && digits.length === 9) return `+254${digits}`;
      return mobile;
    },
    buildUpdatePayload(stepCode) {
      const base = {
        quoteId: parseInt(this.effectiveQuoteId, 10),
        selectedRiskId: this.riskId,
        stepCode,
        prodId: MOTOR_PROD_ID,
        agentId: this.referralAgentId,
        fullName: this.form.fullName,
        email: this.form.email,
        idNumber: this.form.idNumber,
        kraPinNumber: this.form.kraPinNumber,
        mobileNumber: this.normalizeMobileForApi(this.form.mobileNumber),
        poBoxNo: '1245',
        postalCode: '00100',
        pinNumber: this.form.kraPinNumber,
        gender: this.normalizeGenderForApi(this.form.gender),
        addressLine1: '1245',
        coverFrom: this.coverFromFormatted(),
        documents: [],
      };
      if (stepCode === 'PROVIDE_DETAILS') {
        const payload = {
          ...base,
          carDTOS: [{
            ...this.activeRisk.car,
            carId: this.activeRisk.car?.carId || 0,
            riskId: this.riskId,
          }],
        };
        if (this.isComprehensive && this.activeRisk?.additionalBenefits?.length) {
          payload.additionalBenefits = this.activeRisk.additionalBenefits.map((b) => ({
            id: b.id || b.sectionCode || b.sectCode,
            benefit: b.benefit || b.sectionName,
            value: b.value || b.insuredAmount || null,
            noOfBeneficiaries: b.noOfBeneficiaries || 0,
          }));
        }
        return payload;
      }
      const car = {
        ...this.activeRisk.car,
        ...this.vehicle,
        carId: this.activeRisk.car?.carId || 0,
        riskId: this.riskId,
        covertypeId: this.activeRisk.covertypeId,
        evaluationSelected: 2131362803,
        vehicleImages: [],
        engineNo: this.vehicle.engineNumber,
        logBookNumber: this.vehicle.logBookNumber,
        logbookTransferred: this.logbookTransferred === 'yes' ? 'Y' : 'N',
        valuer: this.isComprehensive ? this.selectedValuer : '',
        valuationDate: this.isComprehensive && this.selectedValuer ? formatDate(this.valuationDate) : null,
        valuationLocation: this.isComprehensive ? this.valuerLocationFilter : '',
      };
      return { ...base, carDTOS: [car] };
    },
    async saveDetails() {
      if (!this.canSaveDetails) {
        if (!this.identityFetched) this.$toast.error('Verify your identity first');
        return;
      }
      const form = this.$refs.detailsForm;
      if (form && !form.validate()) {
        this.$toast.error('Please check your contact details');
        return;
      }
      this.saving = true;
      try {
        await this.uploadPendingIdentityDocs();
        const res = await updateMotorQuote(this.buildUpdatePayload('PROVIDE_DETAILS'));
        if (!res.success) {
          this.$toast.error(res.message || 'Could not save details');
          return;
        }
        const { data } = res;
        if (data?.covertypes) {
          this.quote = data;
          this.activeRisk = data.covertypes.find((c) => c.riskId === this.riskId) || this.activeRisk;
        } else if (data) {
          this.quote = { ...this.quote, ...data };
        }
        this.activeStep = 1;
        this.persistProgress();
        this.$toast.success('Details saved');
      } catch (e) {
        this.$toast.error(e?.message || 'Could not save details');
      } finally {
        this.saving = false;
      }
    },
    async saveVehicle() {
      if (!this.vehicleVerified) {
        this.$toast.error('Verify your registration number first');
        return;
      }
      const form = this.$refs.vehicleForm;
      if (form && !form.validate()) {
        this.$toast.error('Please check vehicle details');
        return;
      }
      if (!this.canSaveVehicle) {
        if (!this.uploads.logbook.done) this.$toast.error('Upload your logbook');
        else if (this.isComprehensive && (!this.selectedValuer || !this.valuationDate)) {
          this.$toast.error('Select a valuer and valuation date');
        } else if (!logbookFieldsValid(this.vehicle.logBookNumber, this.logbookTransferred)) {
          this.$toast.error('Enter a valid logbook number (up to 10 letters and numbers)');
        } else if (!this.ntsaFound && !manualVehicleFieldsValid(this.vehicle)) {
          this.$toast.error('Complete all vehicle details with valid values');
        } else {
          this.$toast.error('Complete all required vehicle fields');
        }
        return;
      }
      this.saving = true;
      try {
        await this.uploadPendingLogbook();
        const res = await updateMotorQuote(this.buildUpdatePayload('PROVIDE_DETAILS_VEHICLE'));
        if (!res.success) {
          this.$toast.error(res.message || 'Could not save vehicle');
          return;
        }
        const refreshed = await getMotorQuote(this.effectiveQuoteId);
        if (refreshed.success) {
          this.quote = refreshed.data;
          this.activeRisk = (this.quote.covertypes || []).find((c) => c.riskId === this.riskId) || this.activeRisk;
        }
        this.activeStep = 2;
        this.persistProgress();
      } catch (e) {
        this.$toast.error(e?.message || 'Could not save vehicle');
      } finally {
        this.saving = false;
      }
    },
    async saveBranchAndCreatePolicy() {
      if (!this.branchCode) return;
      this.saving = true;
      const res = await this.initPolicyWithDueAmount();
      this.saving = false;
      if (!res.success) {
        this.$toast.error(res.message || POLICY_PREPARE_ERROR);
        return;
      }
      this.policyCreated = true;
      this.activeStep = 3;
      saveMotorPendingQuote(this.authenticatedUser?.email, {
        quoteId: this.effectiveQuoteId,
        riskId: this.riskId,
        quote: this.quote,
      });
      this.persistProgress();
      this.$toast.success('Ready for payment');
    },
    async initPolicyWithDueAmount() {
      return initMotorQuotationPolicy({
        quoteId: this.effectiveQuoteId,
        activeRisk: this.activeRisk,
        riskId: this.riskId,
        form: this.form,
        vehicle: {
          ...this.vehicle,
          logbookTransferred: this.logbookTransferred,
          valuer: this.isComprehensive ? this.selectedValuer : '',
          valuationDate: this.isComprehensive && this.valuationDate
            ? formatDate(this.valuationDate)
            : undefined,
          valuationLocation: this.isComprehensive ? this.valuerLocationFilter : '',
        },
        branchCode: this.branchCode,
        coverFrom: this.coverFromFormatted(),
        totalPremium: this.amountDueNow,
        agentId: this.referralAgentId,
      });
    },
    async payStk() {
      if (!this.canPay) return;
      this.paying = true;
      this.paybillFallback = false;
      const email = this.authenticatedUser?.email;
      try {
        const policyRes = await this.initPolicyWithDueAmount();
        if (!policyRes.success) {
          this.$toast.error(policyRes.message || POLICY_PREPARE_ERROR);
          this.paying = false;
          return;
        }
        this.policyCreated = true;
        const stkRes = await requestMotorStkPush({
          quoteId: this.effectiveQuoteId,
          payableAmount: this.amountDueNow,
          customerPhoneNumber: this.mpesaPhone,
          transactionReference: this.paymentReference,
          transactionDescription: `rfq-${this.effectiveQuoteId}`,
        });
        if (!stkRes.success) {
          this.$toast.error(stkRes.message || 'Failed to send M-Pesa prompt');
          this.paybillFallback = true;
          this.activePayTab = 'paybill';
          this.paying = false;
          return;
        }
        this.$toast.success('M-Pesa prompt sent. Complete payment on your phone.');
        this.pollPayment(stkRes.data.paymentId, email);
      } catch {
        this.paying = false;
        this.paybillFallback = true;
        this.activePayTab = 'paybill';
        this.$toast.error('Failed to send M-Pesa prompt. Try Paybill.');
      }
    },
    pollPayment(paymentId, email) {
      pollMotorPaymentStatus(paymentId).then((res) => {
        if (!res.success) {
          this.paying = false;
          this.$toast.error(res.message || 'Could not verify payment');
          return;
        }
        const status = res.data?.paymentStatus;
        if (status === 'SUCCESSFUL') {
          this.paying = false;
          this.finishSuccess(email);
        } else if (status === 'FAILED' || status === 'CANCELLED') {
          this.paying = false;
          this.paybillFallback = true;
          this.activePayTab = 'paybill';
          this.$toast.error('Payment failed or was cancelled. You can pay via Paybill.');
        } else {
          this.pollTimer = setTimeout(() => this.pollPayment(paymentId, email), 10000);
        }
      }).catch(() => {
        this.paying = false;
        this.$toast.error('Could not verify payment');
      });
    },
    async verifyPaybill() {
      if (!this.canPayPaybill) return;
      this.paying = true;
      const email = this.authenticatedUser?.email;
      const policyRes = await this.initPolicyWithDueAmount();
      if (!policyRes.success) {
        this.paying = false;
        this.$toast.error(policyRes.message || POLICY_PREPARE_ERROR);
        return;
      }
      this.policyCreated = true;
      const valRes = await motorPaybillApi.confirmMotorPaybillPayment({
        quoteId: this.effectiveQuoteId,
        accountNumber: this.paymentReference,
      });
      this.paying = false;
      if (!valRes.success) {
        this.$toast.error(valRes.message || 'Payment not yet received');
        return;
      }
      this.finishSuccess(email, valRes.message);
    },
    finishSuccess(email, message) {
      this.$toast.success(message || 'Payment received. Your policy will be processed shortly.');
      saveMotorPendingQuote(email, {
        quoteId: this.effectiveQuoteId,
        riskId: this.riskId,
        paymentStatus: true,
        quote: this.quote,
      });
      this.$store.dispatch('portalCache/invalidate', {
        channel: 'customer',
        keys: ['policies', 'client'],
        refresh: true,
        force: true,
      });
      this.$router.push({
        name: this.motorConfig.routes.finalization,
        params: { quoteId: String(this.effectiveQuoteId) },
      });
    },
    goBack() {
      if (this.activeStep === 0) {
        this.$router.push({ name: this.motorConfig.routes.start });
      } else {
        this.activeStep -= 1;
        this.persistProgress();
      }
    },
    startNewSale() {
      clearMotorPendingQuote(this.authenticatedUser?.email);
      this.showNewSaleConfirm = false;
      this.$router.push({ name: this.motorConfig.routes.start });
    },
  },
  watch: {
    activeStep() { this.persistProgress(); },
    paymentPlan() { this.persistProgress(); },
    isComprehensive(val) {
      if (!val && this.paymentPlan !== 'full') {
        this.paymentPlan = 'full';
      }
    },
    coverStartDate(val) {
      const clamped = clampCoverStartDate(val);
      if (clamped !== val) this.coverStartDate = clamped;
      this.vehicleVerified = false;
      this.verifyError = null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/motor-sale-shared.scss';

.motor-page { max-width: 1200px; margin: 0 auto; padding: 20px 28px 32px; }
.motor-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
.motor-header-left { display: flex; align-items: flex-start; gap: 12px; }
.back-btn {
  width: 36px; height: 36px; border-radius: 50%; border: 1px solid #E2E8F0;
  background: white; display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
}
.btn-new-sale {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid #CBD5E1;
  background: #F8FAFC;
  color: #1E4276;
  font-size: 0.78rem;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  white-space: nowrap;
}
.btn-new-sale:hover { background: #EEF2FF; }
.loading-wrap { text-align: center; padding: 48px; }
.quote-layout { display: grid; grid-template-columns: minmax(0, 1fr) 300px; gap: 24px; }
.quote-layout--checkout { grid-template-columns: 1fr !important; max-width: 720px; margin: 0 auto; width: 100%; }
.field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
@media (max-width: 720px) { .field-grid { grid-template-columns: 1fr; } .quote-layout { grid-template-columns: 1fr; } }
.branch-loading { display: block; margin: 24px auto; }
.branch-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
  max-height: min(52vh, 480px);
  overflow-y: auto;
  padding: 2px 4px 12px;
  margin-bottom: 4px;
}
.branch-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 14px 16px;
  border: 1.5px solid #e8edf2;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.branch-card:hover { border-color: #b8c5d4; background: #f8fafc; }
.branch-card--selected {
  border-color: #00aeef;
  background: #f0f9ff;
  box-shadow: 0 0 0 1px #00aeef;
}
.branch-card-indicator {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 2px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.branch-card--selected .branch-card-indicator { border-color: #00aeef; }
.branch-card-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: transparent;
}
.branch-card--selected .branch-card-dot { background: #00aeef; }
.branch-card-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}
@media (max-width: 720px) {
  .branch-grid { grid-template-columns: 1fr; max-height: none; }
}
.new-sale-dialog {
  background: white;
  border-radius: 14px;
  box-shadow: 0 20px 40px rgba(2, 6, 23, 0.25);
  overflow: hidden;
}
.nsd-header {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 16px 16px 10px;
  border-bottom: 1px solid #E2E8F0;
}
.nsd-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #f59e0b, #ef4444);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.nsd-title { margin: 0; font-size: 1rem; font-weight: 700; color: #0F172A; }
.nsd-sub { margin: 2px 0 0; font-size: 0.8rem; color: #64748B; }
.nsd-body { padding: 12px 16px; }
.nsd-desc { margin: 0; font-size: 0.82rem; color: #334155; line-height: 1.5; }
.nsd-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 16px 16px;
}
.nsd-btn-cancel, .nsd-btn-confirm {
  border: none;
  border-radius: 9px;
  padding: 10px 14px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
}
.nsd-btn-cancel { background: #F1F5F9; color: #334155; }
.nsd-btn-cancel:hover { background: #E2E8F0; }
.nsd-btn-confirm {
  background: linear-gradient(135deg, #2BABE2, #1E4276);
  color: white;
  display: inline-flex;
  align-items: center;
}
.nsd-btn-confirm:hover { filter: brightness(0.95); }
.quote-sidebar { position: sticky; top: 20px; }

@media (max-width: 960px) {
  .motor-page { padding: 16px 16px 28px; }
  .quote-layout { grid-template-columns: 1fr; gap: 16px; }
  .quote-sidebar { position: static; }
}

@media (max-width: 600px) {
  .motor-page { padding: 12px 12px 88px; }
  .motor-header {
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
  }
  .motor-header-left { min-width: 0; flex: 1 1 auto; }
  .btn-new-sale {
    padding: 7px 10px;
    font-size: 0.72rem;
  }
  .nsd-footer {
    flex-direction: column-reverse;
    gap: 8px;
  }
  .nsd-btn-cancel,
  .nsd-btn-confirm {
    width: 100%;
    justify-content: center;
  }
  .branch-grid {
    grid-template-columns: 1fr;
    max-height: none;
  }
}
</style>

<style lang="scss">
@import '@/styles/motor-customer.scss';
</style>
